/**
 * Feature tier configuration for CARTIEAI
 * 
 * Tiers:
 * - 'starter' (free): Core features available to all users
 * - 'pro' ($50/mo): Advanced analytics and team features
 * - 'enterprise' (Contact Sales): Full platform with automation and integrations
 * 
 * Demo account (demo@cartieai.com) bypasses all tier restrictions.
 * Admin accounts bypass all tier restrictions.
 */

export const TIER_CONFIG = {
  starter: {
    label: 'Starter',
    price: '$20/mo',
    color: 'emerald',
    badge: null, // No badge needed - it's the default
  },
  pro: {
    label: 'Pro',
    price: '$50/mo',
    color: 'blue',
    badge: 'Pro',
  },
  enterprise: {
    label: 'Enterprise',
    price: 'Custom',
    color: 'violet',
    badge: 'Enterprise',
  }
};

// Map of route paths to their required tier
// Routes not listed here default to 'starter' (free)
export const FEATURE_TIERS = {
  // === STARTER (Free) - Core features ===
  '/dashboard': 'starter',
  '/ai-advisor': 'starter',
  '/anomaly-detection': 'starter',
  '/cost-forecasting': 'starter',
  '/one-click-fix': 'starter',
  '/carbon': 'starter',
  '/integrations': 'starter',
  '/settings': 'starter',
  '/comparison': 'starter',
  '/recommendations': 'starter',
  '/guardrails': 'starter',
  '/health-score': 'starter',
  '/notifications': 'starter',
  '/export': 'starter',
  '/reports': 'starter',

  // === PRO ($50/mo) - Advanced analytics & teams ===
  '/cloud-pl': 'pro',
  '/tech-spend': 'pro',
  '/customer-economics': 'pro',
  '/cost-dna': 'pro',
  '/cost-graph': 'pro',
  '/ai-command-center': 'pro',
  '/weather': 'pro',
  '/search': 'pro',
  '/ai-gpu': 'pro',
  '/industry': 'pro',
  '/bill-shock': 'pro',
  '/teams': 'pro',
  '/tagging': 'pro',
  '/savings': 'pro',
  '/leaderboard': 'pro',
  '/collaboration': 'pro',
  '/cost-bot': 'pro',
  '/cost-explainer': 'pro',
  '/dashboard-builder': 'pro',

  // === ENTERPRISE (Contact Sales) - Full platform ===
  '/spend-freeze': 'enterprise',
  '/cost-conscience': 'enterprise',
  '/ai-cfo': 'enterprise',
  '/peer-benchmark': 'enterprise',
  '/pilot': 'enterprise',
  '/voice-assistant': 'enterprise',
  '/terraform-preview': 'enterprise',
  '/calculator': 'enterprise',
  '/scheduler': 'enterprise',
  '/time-machine': 'enterprise',
  '/git-to-bill': 'enterprise',
  '/pr-cost-impact': 'enterprise',
  '/kubernetes': 'enterprise',
  '/saas-spend': 'enterprise',
  '/snowflake': 'enterprise',
  '/snowflake-queries': 'enterprise',
  '/snowflake-alerts': 'enterprise',
  '/spike-autopsy': 'enterprise',
  '/query-estimator': 'enterprise',
  '/budget-countdown': 'enterprise',
  '/team-chargeback': 'enterprise',
  '/storage-detective': 'enterprise',
  '/compliance': 'enterprise',
  '/security-center': 'enterprise',
};

/**
 * Get the tier required for a given path
 */
export const getFeatureTier = (path) => {
  return FEATURE_TIERS[path] || 'starter';
};

/**
 * Check if a user's subscription tier can access a feature
 */
export const canAccessTier = (userTier, requiredTier) => {
  const tierOrder = { starter: 0, pro: 1, enterprise: 2 };
  return (tierOrder[userTier] || 0) >= (tierOrder[requiredTier] || 0);
};

/**
 * Get badge info for a tier
 */
export const getTierBadge = (tier) => {
  if (tier === 'starter') return null;
  return TIER_CONFIG[tier] || null;
};
